/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import config from '../../../../share/config';
import session from '../../../../share/session';
import error from '../../../../common/nodejs/errors';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import util from '../../../../share/util/utils';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  CONFIRMATION: 'confirmation',
  FINISH_CONFIRMATION: 'finishConfirmation',
  FINISH: 'finish',
  RETRY: 'retry',
  OVER_SPEED: 'overSpeed',
  UNABLE_TO_START: 'unableToStart',
  UNABLE_TO_END: 'unableToEnd',
  FAILED: 'failed'
};

/** 待ち時間定義 */
const WAIT_TIME = {
  TIME_1000: 1000,
  TIME_8000: 8000
};

/** 現在セッション */
const SESSION_PROPERTY = {
  DEFAULT: 0,
  NON_DEFAULT: 1
};

export default {
  name: 'requirement141',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      systemId: '',
      executeWaitTime: { wait_time1: '', wait_time2: '' },
      timer: null,
      isFirst: true,
      modal_requirement_141: false,
      sessionProperty: SESSION_PROPERTY.DEFAULT
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
        case PHASE.OVER_SPEED:
        case PHASE.UNABLE_TO_START:
        case PHASE.UNABLE_TO_END:
          return 'danger';
        case PHASE.FINISH:
          return 'success';
        case PHASE.START:
        case PHASE.CONFIRMATION:
        case PHASE.FINISH_CONFIRMATION:
        case PHASE.RETRY:
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /**
     * 初期化処理
     */
    init() {
      this.modal_requirement_141 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.startProcess();
    },

    /**
     * Wizardを閉じる
     */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_141 = false;
    },
    
    /**
     * exit処理(要求)
     * @param {boolean} isError エラー遷移の有無 
     */
    async exit(isError) {
      try {
        if (this.timer) {
          clearTimeout(this.timer);
          this.timer = null;
        }
        this.isLoading = true;
        const token = session.getToken();
        const postParameter = {
          id_token: token.id_token,
          type: 'exit'
        };
        if(!this.isFirst){
          postParameter.is_reuse_exit = true;
        }
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          // POST リクエストの body を指定する
          postParameter,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (isError) {
          if (response) {
            // エラーコールバックが呼ばれず、completed / failed が返ってきてポーリングが終了した時のみ、最終的な GET response が返ってくる。
            // エラーコールバックが呼ばれた場合は、null になる。
            switch (response.data.status) {
              case config.STATUS_WSP_COMPLETED:
                // 現在セッション更新（DefaultSession）
                this.sessionProperty = SESSION_PROPERTY.DEFAULT;
                // disposeAPIを実施してモーダルを閉じる
                await this.dispose();
                this.closeWizard();
                break;
              case config.STATUS_WSP_FAILED:
              default:
                // 共通エラーへ遷移
                this.showUnexpectedError();
                break;
            }
          }
        } else {
          // エラー遷移なし：disposeAPIを実施して機能終了
          await this.dispose();
          this.closeWizard();
          return;
        }
      } finally {
        this.isLoading = false;
        this.isFirst = false;
      }
    },

    /** 終了時の処理 */
    async dispose() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          // POST リクエストの body を指定する
          {
            id_token: token.id_token,
            type: 'dispose'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

      } finally {
        this.isLoading = false;
      }
    },

    /**
     * その他エラー発生時の表示
     */
    showUnexpectedError() {
      this.phase = PHASE.FAILED;
    },

    /**
     * 指定時間経過後のタイムアウト処理を伴う画面遷移
     * @param {*} time 経過時間
     * @param {*} nextPhase 次遷移先
     */
    transitionProcessWithTimeout(time, nextPhase) {
      this.phase = nextPhase;
      // 指定時間経過後、タイムアウト処理に遷移
      this.timer = setTimeout(async () => await this.execTimeout(), time);
    },

    /** 待機遷移処理 */
    async startProcess() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
      this.phase = PHASE.START;
      // 1000ms経過後、確認処理へ遷移
      await util.sleep(WAIT_TIME.TIME_1000);
      this.transitionProcessWithTimeout(WAIT_TIME.TIME_8000, PHASE.CONFIRMATION);
    },

    /**
     * 終了確認遷移処理
     */
    finishConfirmationProcess() {
      this.phase = PHASE.FINISH_CONFIRMATION;
    },

    /**
     * 終了遷移処理
     */
    async finishProcess() {
      this.phase = PHASE.FINISH;
      // 指定時間経過後、再実行処理へ遷移
      await util.sleep(this.executeWaitTime.wait_time1);
      this.transitionProcessWithTimeout(this.executeWaitTime.wait_time2, PHASE.RETRY);
    },

    /** 共通エラー画面 OK押下処理 */
    onFailedOkClicked() {
      // 現在セッションがNonDefaultSessionの場合はterminateを実行する
      if (this.sessionProperty === SESSION_PROPERTY.NON_DEFAULT) {
        this.exit(false);
      } else {
        this.dispose();
        this.closeWizard();
      }
    },

    /** 確認処理(要求&取得) */
    async confirm() {
      try {
        if (this.timer) {
          clearTimeout(this.timer);
          this.timer = null;
        }
        this.isLoading = true;
        const token = session.getToken();
        const postParameter = {
          id_token: token.id_token,
          type: 'session_transition'
        };
        if(!this.isFirst){
          postParameter.is_reuse_exit = true;
        }
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          // POST リクエストの body を指定する
          postParameter,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // エラーコールバックが呼ばれず、completed / failed が返ってきてポーリングが終了した時のみ、最終的な GET response が返ってくる。
          // エラーコールバックが呼ばれた場合は、null になる。
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 現在セッション更新（NonDefaultSession）
              this.sessionProperty = SESSION_PROPERTY.NON_DEFAULT;
              // セキュリティアクセス判定
              await this.secAccess();
              break;
            case config.STATUS_WSP_FAILED:
              // 異常終了
              switch (parseInt(response.data.async_code)) {
                case error.WSP_EXCESSIVE_WHEEL_SPEED_ERROR:
                  // 車輪速超過処理へ遷移
                  this.transitionProcessWithTimeout(response.data.user_data.wait_time, PHASE.OVER_SPEED);
                  break;
                default:
                  // 終了不可処理へ遷移
                  this.transitionProcessWithTimeout(response.data.user_data.wait_time, PHASE.UNABLE_TO_END);
                  break;
              }
              break;
            default:
              // 該当のエラーコードが存在しない場合、汎用エラー
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
        this.isFirst = false;
      }
    },

    /** セキュリティアクセス判定 */
    async secAccess() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          // POST リクエストの body を指定する
          {
            id_token: token.id_token,
            type: 'sec_access'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 実行処理へ遷移
              await this.execute();
              break;
            case config.STATUS_WSP_FAILED:
              // 各エラー表示
              switch (response.data.async_code) {
                case error.WSP_GENERAL_ERROR:
                default:
                  // 該当のエラーコードが存在しない場合、共通エラー
                  // ありえないが定義しておく
                  this.showUnexpectedError();
                  break;
              }
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 実行処理(要求&取得) */
    async execute() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          // POST リクエストの body を指定する
          {
            id_token: token.id_token,
            type: 'execute'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // エラーコールバックが呼ばれず、completed / failed が返ってきてポーリングが終了した時のみ、最終的な GET response が返ってくる。
          // エラーコールバックが呼ばれた場合は、null になる。
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              this.executeWaitTime = {
                wait_time1: response.data.user_data.wait_time1,
                wait_time2: response.data.user_data.wait_time2
              };
              // 終了確認処理に遷移
              this.finishConfirmationProcess();
              break;
            case config.STATUS_WSP_FAILED:
              // 異常終了
              switch (parseInt(response.data.async_code)) {
                case error.WSP_141_UNABLE_TO_START:
                  // 開始不可処理へ遷移
                  this.transitionProcessWithTimeout(response.data.user_data.wait_time, PHASE.UNABLE_TO_START);
                  break;
                case error.WSP_141_UNABLE_TO_END:
                  // 終了不可処理へ遷移
                  this.transitionProcessWithTimeout(response.data.user_data.wait_time, PHASE.UNABLE_TO_END);
                  break;
                default:
                  // 該当のエラーコードが存在しない場合、汎用エラー
                  this.showUnexpectedError();
                  break;
              }
              break;
            default:
              // 該当のエラーコードが存在しない場合、汎用エラー
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * タイムアウト時処理
     */
    async execTimeout() {
      this.isLoading = false;
      clearTimeout(this.timer);
      this.timer = null;
      await this.exit(true);
    },

    /**
      * API 通信で共通バリデーションでエラーが発生した際のコールバック
      * @param {object} result バリデーション結果
      */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },
  },

  /**
   * DOM作成後実行処理
   */
  async mounted() {
    // 初期化
    this.init();
  },
};